import { Injectable, inject } from '@angular/core';
import { SessionUser, UserClientVm, DeleteUserByFirm, ResetPasswordData, LeaveFirm } from '../../store/user/user.interface';
import { HttpBaseService } from './http-base.service';
import { Store } from '@ngrx/store';
import { getSessionUser } from '../../store/user/user.selector';
import { lastValueFrom } from 'rxjs';
import { Reviewer } from '../../modules/client/dashboard/people/people.interface';
import { UserRoles } from '../constants/constants';
import { FirmContextService } from './firm-context.service';

@Injectable({
  providedIn: 'root'
})

export class UserService {

  /** MP-5643: the FM's chosen acting child firm, when a switcher exists. */
  private readonly firmContext = inject(FirmContextService);
  
  userObject!: UserClientVm;

  constructor(private http:HttpBaseService, private store: Store<SessionUser>) { 
    this.store.select(getSessionUser).subscribe((user:UserClientVm)=>{
      this.userObject = user;
    });
  }

  getSessionUser() {
    return this.userObject; 
  }

  getUserById(id:string){
    return lastValueFrom(this.http.get(`users/${id}`));
  }

  approveUser(reviewerId: string) {
    const url = `users/${reviewerId}/approve`
    return this.http.put(url);
  }
  manualRejectEducation(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/rejectBarEducationManual`
    return lastValueFrom(this.http.post(url,payload));
  }
  manualApproveEducation(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/approveBarEducationManual`
    return lastValueFrom(this.http.post(url,payload));
  }

  manualRejectCredential(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/rejectCredentialManual`
    return lastValueFrom(this.http.post(url,payload));
  }
  manualApproveCredential(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/approveCredentialManual`
    return lastValueFrom(this.http.post(url,payload));
  }

  // bulk approval/rejection for education and credentials

  manualRejectEducationBulk(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/rejectBarEducationManualBulk`
    return lastValueFrom(this.http.post(url,payload));
  }
  manualApproveEducationBulk(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/approveBarEducationManualBulk`
    return lastValueFrom(this.http.post(url,payload));
  }

  manualRejectCredentialBulk(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/rejectCredentialManualBulk`
    return lastValueFrom(this.http.post(url,payload));
  }
  manualApproveCredentialBulk(reviewerId: string,payload:any) {
    const url = `users/${reviewerId}/approveCredentialManualBulk`
    return lastValueFrom(this.http.post(url,payload));
  }

  deleteUserByFirm(reviewerId: string, data: DeleteUserByFirm) {
    const url = `users/archiveUser/${reviewerId}`
    return this.http.delete(url, data);
  }

  leaveFirmPermenant(reviewerId: string, data: LeaveFirm) {
    const url = `users/deleteReviewerByFirm/${reviewerId}`
    return this.http.delete(url, data);
  }

  restoreUserByFirm(reviewerId: string) {
    const url = `users/restoreArchivedReviewerByFirm/${reviewerId}`
    return lastValueFrom(this.http.put(url));
  }

  getUserId(){
    return this.userObject._id;
  }

  getUserRole(){
    return this.userObject.role;
  }
  
  getPositions(){
    return this.http.get('positions');
  }

  /**
   * The firm the user is ACTING as — the switcher's selected child for a
   * root-attached FM, otherwise their own firm.
   *
   * MP-5643: this used to return `userObject.firm._id` unconditionally, which
   * for a parent-level FM is the ROOT. Every caller of this accessor passes it
   * to the API as "the firm I am working in" — project lists, job boards,
   * talent pools, messages, search markets — and all of those are WORK, which
   * under the family model belongs to the acting child. Returning the root
   * meant the switcher changed the header while every explicit firmId stayed
   * on the parent, so screens silently showed nothing.
   *
   * Safe to change in place because the IDENTITY callers already moved off
   * this accessor in MP-5641: anything matching a reviewer's root-keyed
   * `tenants[].firm` uses `getFamilyRootFirmId()` instead. If you need the
   * root, use that; this one deliberately follows the acting context.
   *
   * For a standalone firm, and for a root FM who has selected nothing, the
   * acting firm IS their own firm — so Staftr and PWC are unchanged.
   */
  getUserFirmId(){
    return this.getActingChildFirmId() || (this.userObject?.firm?._id) || '';
  }

  resetPassword (data:ResetPasswordData) {
    return this.http.put('auth/resetPassword', data);
  };

  verifyPhone(body: any ) {
    return this.http.put('users/verifyPhone', body);
  }

  getAbdetails(reviewerId: string) {
    return this.http.get(`users/${reviewerId}/abDetails`);
  }

  invokeBackgroundCheck(reviewerId:string, body:any) {
    return this.http.post(`users/${reviewerId}/manualCheck`,  body);
  };

  invokeCriminalCheck(reviewerId:string, body:any) {
    return this.http.post(`users/${reviewerId}/criminalCheck`,  body);
  };

  invokeEducationBackgroundCheck(reviewerId:string, body:any) {
    return this.http.post(`users/${reviewerId}/approveEducation`, body);
  };

  notInterestedConfirmation(hash: string, payload: {firm: string}) {
    return lastValueFrom(this.http.delete(`auth/deleteReviewerByReviewerFromFirm/${hash}`, payload));
  };

  getNotificationPreference() {
    return this.http.get('users/getNotificationPreference');
  }

  /**
   * MP-5638/MP-5636: the FAMILY ROOT of the firm the user is acting as.
   *
   * A reviewer holds ONE tenant entry per family and it is keyed to the ROOT,
   * while the acting firm may be a CHILD. Matching an entry against the acting
   * firm id therefore finds nothing for a child-firm user - which is why the
   * people list rendered empty from a perfectly good API response.
   *
   * MP-5641 added a SECOND class of caller: parent-owned TAXONOMY. Dynamic
   * credentials, criteria, job types and search markets are defined at the
   * root too, so any client-side filter matching a definition's `firm` against
   * the acting firm must use this, not `getUserFirmId()`.
   *
   * What must NOT use it: a firm's SELECTION of taxonomy
   * (`globalCriteria`/`globalCredential`, `selectedSingle*Id`) and work items
   * (projects, positions, documents). Those stay keyed to the acting child -
   * that is what lets two children of one parent differ.
   *
   * `parentFirmId` is null for a root or standalone firm, so this returns the
   * firm's own id and every childless firm behaves exactly as before.
   */
  getFamilyRootFirmId(): string {
    const firm: any = this.userObject?.firm;

    return firm?.parentFirmId || firm?._id;
  }

  /**
   * MP-5736: the CHILD firm whose own view of a reviewer we are editing, or
   * '' when there is no child context.
   *
   * The counterpart of `getFamilyRootFirmId()`, pointing the opposite way. The
   * root answers "which reviewer pool is this?"; this answers "whose private
   * view of that reviewer am I editing?". MP-5734 keeps those edits in a
   * separate `FirmProfileEdit` collection so an FM working in child A cannot
   * write into a record every sibling child reads, and this is the value the
   * API needs in order to route the write there.
   *
   * RETURNING '' IS MEANINGFUL, NOT A FALLBACK. The server reads an absent
   * `actingFirmId` as "use today's `tenants[]` path", so every caller keeps
   * its current behaviour byte-for-byte unless this returns an id:
   *
   *   reviewer (self-service)   -> '' : a reviewer edits their own shared
   *                                     record, never a firm's view of them
   *   admin                     -> '' : cross-firm by design, no child context
   *   standalone or root firm   -> '' : `parentFirmId` is null, so there is no
   *                                     child to attribute the edit to
   *   user attached to a CHILD  -> that child's id
   *
   * That last line is the whole answer today for a PROJECT_OWNER, which is
   * what MP-5736 item 5 asks for: a PM's acting firm IS their own child.
   *
   * WHAT IS DELIBERATELY STILL MISSING. A parent-level FM has no child context
   * yet, because the child-firm switcher is MP-5643 and does not exist. Their
   * firm is the root, so this returns '' and their saves keep going to the
   * shared tenant entry exactly as they do now. When MP-5643 lands, the
   * switcher's selection is the only thing that changes here - one method
   * rather than every call site, which is why this is centralised instead of
   * read from `userObject` at each caller.
   */
  getActingChildFirmId(): string {
    const role = this.userObject?.role;
    const isFirmSideUser = role === UserRoles.firmManager
      || role === UserRoles.projectManager
      || role === UserRoles.projectOwner;

    if (!isFirmSideUser) {
      return '';
    }

    const firm: any = this.userObject?.firm;

    /**
     * A user attached directly to a CHILD is ALREADY acting in it and must
     * never be switched out of it - that is their whole scope. This covers
     * the PROJECT_OWNER case (MP-5736 item 5) and a legacy child-attached FM,
     * and it is checked FIRST so a stale switcher selection left in storage
     * can never override a child-scoped user's own firm.
     */
    if (firm?.parentFirmId) {
      return firm._id;
    }

    /**
     * MP-5643: a ROOT-attached user takes the switcher's choice. This is the
     * only source of a child context for a parent-level FM - the reviewer
     * cannot supply it (their tenant entry is root-keyed) and neither can the
     * user record, whose firm IS the root.
     *
     * No selection (or a standalone firm, where there is nothing to select)
     * returns '' and the server takes today's path.
     */
    return this.firmContext.selectedChildId() || '';
  }

  /**
   * MP-5736: the display name for the "added by <firm>" marker on a
   * child-firm record, or '' when there is no child context.
   *
   * Paired with `getActingChildFirmId()` so the two can never disagree: a
   * marker is only ever shown for records the acting child owns, and the
   * acting child is always the user's own firm today. When MP-5643's switcher
   * lands, both read the selection instead and every call site is unchanged.
   */
  /**
   * MP-5643: is this user attached to the family ROOT (or a standalone firm)?
   *
   * Only such a user may switch child context. A user attached to a CHILD -
   * a PROJECT_OWNER, or a legacy child-attached FM - is scoped to that one
   * child: `isProjectOwnerOf` does not resolve to the root, and an FM on a
   * child roster is not on `root.firmManagers`, so either would get a 403 on
   * every request after switching to a sibling.
   */
  isFamilyRootUser(): boolean {
    const firm: any = this.userObject?.firm;

    return !!firm && !firm.parentFirmId;
  }

  getActingChildFirmName(): string {
    if (!this.getActingChildFirmId()) {
      return '';
    }

    // MP-5643: when the switcher supplied the id, it also knows the name.
    const switcherName = this.firmContext.selectedChildName();
    if (switcherName) {
      return switcherName;
    }

    const firm: any = this.userObject?.firm;

    return firm?.name || '';
  }

  /**
   * MP-5638/MP-5641: may the acting user ADMINISTER taxonomy - dynamic
   * credentials and criteria, job types, search markets?
   *
   * Mirrors the server gate (`requireTaxonomyWrite` ->
   * `firmAuthorizationService.canWriteTaxonomy`). The app's convention is that
   * an action a user cannot perform is not shown, so this drives the create,
   * edit, archive and delete controls on the settings screens rather than
   * letting them submit into a 403.
   *
   * THE DISCRIMINATOR IS THE FIRM, NOT THE ROLE - which is the opposite of
   * what the rule reads like, so it is worth stating plainly.
   *
   * Checking `role === FIRM_MANAGER` is useless here, because a child firm's
   * own user IS a FIRM_MANAGER: that is how `endpoints/firmManagers.js`
   * created them before MP-5638, and how the QA family seed still creates them
   * by default. The server does not ask "is this user an FM?" - it asks "is
   * this user on the FAMILY ROOT's roster?", and an FM sitting on a child's
   * roster fails that.
   *
   * A firm-side user cannot change which firm they act as - there is no firm
   * switcher, only the reviewer-side tenant picker - so their acting firm is
   * always their own `profile.firm`. Therefore:
   *
   *   acting firm has a parent  -> they are scoped to a CHILD, so NO. A
   *                                correctly attached FM's `profile.firm` is
   *                                the root (MP-5638), so a firm-side user
   *                                whose own firm has a parent is either a PM
   *                                or an FM the server will refuse anyway.
   *   root or standalone firm   -> YES, unchanged from today.
   *   ADMIN                     -> YES, the cross-firm role by design.
   *
   * That middle line is the one to be careful with. A PROJECT_OWNER CAN write
   * taxonomy at a standalone firm today - the ACL seed grants PROJECT_OWNER
   * `'*'` - so refusing every PM would be a live regression for Staftr and
   * PWC, where every firm is childless.
   *
   * KNOWN GAP, deliberately left: a ROOT that HAS children is indistinguish-
   * able from a standalone firm on the client, so a PM on such a root still
   * sees the controls and is refused on submit. That is exactly today's
   * behaviour, so it is never worse than before. Closing it needs a
   * server-supplied boolean on the session payload.
   */
  canManageTaxonomy(): boolean {
    if (this.userObject?.role === UserRoles.admin) {
      return true;
    }

    const firm: any = this.userObject?.firm;

    return !firm?.parentFirmId;
  }


  /**
   * The acting user's tenant entry on a reviewer.
   *
   * Compares against the family ROOT, not the acting firm. Entries carry
   * `firm` either as a raw id string or as a populated document depending on
   * the endpoint, so both shapes are handled.
   */
  getUserCurrentTenant(user: Reviewer){
    const rootFirmId = this.getFamilyRootFirmId();

    return user.tenants.find((tenant: any) => {
      const isFirmString = typeof tenant.firm === 'string';
      return isFirmString ? tenant.firm === rootFirmId : tenant.firm?._id === rootFirmId
    })
  }

  getReviewersWorkedTogetherCounts(payload: {reviewerIds: string[], firmId: string}) {
    return lastValueFrom(this.http.post('users/getReviewersWorkedTogetherCounts', payload));
  }

  markRequestCompleted(payload: any): any {
    return lastValueFrom(this.http.post(`users/requestCompleted/${payload.firmId}`, payload));
  }
}
